<template>
  <div>
    <q-card>
      <q-img
        v-if="program.coverphoto"
        :src="program.coverphoto"
        no-transition
        :ratio="24/9"
        class="cursor-pointer coverphoto"
        @click="onShowProgramInfo"
      >
        <div class="absolute-bottom text-h6 q-pa-sm">
          {{ program.programName }}
        </div>
        <template #error>
          <div class="absolute-bottom text-h6 q-pa-sm">
            {{ program.programName }}
          </div>
        </template>
      </q-img>
      <q-card-section
        v-ripple
        class="q-pt-xs row cursor-pointer"
        clickable
        @click="onShowProgramInfo"
      >
        <div
          v-if="!program.coverphoto"
          class="col-12 q-pt-sm"
        >
          <p class="text-subtitle1 ellipsis q-ma-none">
            {{ program.programName }}
          </p>
        </div>
      </q-card-section>
      <q-card-section
        v-ripple
        class="q-pt-none cursor-pointer"
        clickable
        @click="onShowProgramInfo"
      >
        <div class="ellipsis-2-lines">
          {{ personalize(program.objective) }}
        </div>
      </q-card-section>
      <q-card-section
        v-if="showTargetChips"
        class="row q-pt-none q-pb-sm"
      >
        <div class="col">
          <q-chip
            outline
            size="sm"
            icon="assignment"
            :label="probeTargets"
            color="warning"
            text-color="white"
          >
            <q-tooltip>
              {{ $tc('programs.programCard.probeTargets', probeTargets, { count: probeTargets }) }}
            </q-tooltip>
          </q-chip>
          <q-chip
            outline
            size="sm"
            icon="school"
            :label="acquisitionTargets"
            color="primary"
            text-color="white"
          >
            <q-tooltip>
              {{
                $tc('programs.programCard.acquisitionTargets', acquisitionTargets, {
                  count: acquisitionTargets
                })
              }}
            </q-tooltip>
          </q-chip>
          <q-chip
            outline
            size="sm"
            icon="emoji_events"
            :label="masteredAndClosedTargets"
            color="positive"
            text-color="white"
          >
            <q-tooltip>
              {{
                $tc('programs.programCard.masteredAndClosedTargets', masteredAndClosedTargets, {
                  count: masteredAndClosedTargets
                })
              }}
              {{
                $tc('programs.programCard.closedTargets', closedTargets, {
                  count: closedTargets,
                })
              }}
            </q-tooltip>
          </q-chip>
        </div>
      </q-card-section>
      <q-card-actions
        v-if="showActions"
        class="q-mx-sm q-pb-md"
      >
        <div class="col q-mr-md">
          <q-btn-dropdown
            v-if="hasSessions"
            color="primary"
            icon="playlist_add"
            size="sm"
            class="full-width"
            :data-e2e="'button-add-' + String(program.programName || 'unknown').replace(/ /g, '-') + '-to-session'"
            :label="$t('programs.programCard.buttonAddToSession')"
          >
            <q-list v-if="!offlineMode">
              <q-item
                v-for="session in sessions"
                :key="session.id"
                v-ripple
                v-close-popup
                clickable
                class="items-center"
                @click="onAddToSession(session)"
              >
                <q-item-label>
                  {{ session.sessionName }}
                </q-item-label>
              </q-item>
              <q-separator />
              <q-item
                v-close-popup
                v-ripple
                clickable
                class="items-center"
                @click="onAddToSession()"
              >
                <q-item-label>
                  {{ $t('programs.programCard.itemCreateNewSession') }}
                </q-item-label>
              </q-item>
            </q-list>
            <q-list v-else>
              <q-item
                v-close-popup
                v-ripple
                clickable
                class="items-center"
                @click="onAddToSession()"
              >
                <q-item-label>
                  {{ $t('programs.programCard.itemCreateNewSession') }}
                </q-item-label>
              </q-item>
            </q-list>
          </q-btn-dropdown>
          <q-btn
            v-else
            :label="actionLabel"
            :color="actionColor"
            :icon="actionIcon"
            size="sm"
            class="full-width"
            :data-e2e="'button-add-' + String(program.programName || 'unknown').replace(/ /g, '-') + '-to-session'"
            @click="onAction"
          >
            <q-tooltip>
              {{ actionTooltip }}
            </q-tooltip>
          </q-btn>
        </div>
        <div class="col-auto">
          <q-btn
            flat
            color="primary"
            icon="more_horiz"
            size="sm"
            class="q-ml-md full-width"
          >
            <q-tooltip v-if="!showMenu">
              {{ $t('programs.programCard.tooltipMoreOptions') }}
            </q-tooltip>
            <q-menu v-model="showMenu">
              <q-list style="min-width: 100px">
                <q-item
                  v-ripple
                  v-close-popup
                  clickable
                  @click="onShowProgramInfo"
                >
                  <q-item-section avatar>
                    <q-icon name="info" />
                  </q-item-section>
                  <q-item-section>{{ $t('programs.programCard.listItemPreview') }}</q-item-section>
                </q-item>
                <q-item
                  v-ripple
                  v-close-popup
                  clickable
                  :to="'/programs/edit/' + program.id"
                  :disable="offlineMode"
                >
                  <q-item-section avatar>
                    <q-icon name="edit" />
                  </q-item-section>
                  <q-item-section>{{ $t('programs.programCard.listItemEdit') }}</q-item-section>
                </q-item>
                <q-item
                  v-ripple
                  v-close-popup
                  clickable
                  :disable="offlineMode"
                  @click="manuallyAddProgramModification(program)"
                >
                  <q-item-section avatar>
                    <q-icon name="archive" />
                  </q-item-section>
                  <q-item-section>
                    {{ $t('programs.programCard.listItemLogMod') }}
                  </q-item-section>
                </q-item>
                <q-item
                  v-if="showDeleteProgram"
                  v-ripple
                  v-close-popup
                  :disable="offlineMode"
                  clickable
                  @click="onDeleteProgram"
                >
                  <q-item-section
                    avatar
                  >
                    <q-icon
                      name="delete"
                    />
                  </q-item-section>
                  <q-item-section>{{ $t('programs.programCard.listItemDelete') }}</q-item-section>
                </q-item>
              </q-list>
            </q-menu>
          </q-btn>
        </div>
      </q-card-actions>
    </q-card>
  </div>
</template>
<script>
import ProgramModificationsDialog from 'components/Programs/ProgramModificationsDialog';
import { get, isEmpty, size } from 'lodash';
import { mapActions, mapState } from 'pinia';
import ProgramInfo from 'src/components/Programs/ProgramInfo';
import Interactions from 'src/mixins/Interactions';
import Personalize from 'src/mixins/Personalize';
import { network } from 'src/store/network';
import { programs } from 'src/store/programs';

export default {
  name: 'ProgramCard',

  mixins: [Personalize, Interactions],

  props: {
    program: {
      type: Object,
      required: true,
    },

    sessions: {
      type: Array,
      required: false,
      default: () => [],
    },

    showTargetChips: {
      type: Boolean,
      required: false,
      default: false,
    },

    showActions: {
      type: Boolean,
      required: true,
    },

    showDeleteProgram: {
      type: Boolean,
      default: true,
    },

    actionColor: {
      type: String,
      default: 'primary',
    },
    actionLabel: {
      type: String,
      default: '',
    },
    actionIcon: {
      type: String,
      default: null,
    },
    actionTooltip: {
      type: String,
      default: null,
    },
  },

  emits: ['add-to-session', 'action'],

  data() {
    return {
      hover: false,
      showMenu: false,
    };
  },

  computed: {
    ...mapState(network, ['offlineMode']),

    hasSessions() {
      return size(this.sessions) > 0;
    }, // hasSessions()

    probeTargets() {
      return this.targetsInStatus('probe');
    }, // probeTargets()

    acquisitionTargets() {
      return this.targetsInStatus('acquisition');
    }, // acquisitionTargets()

    masteredTargets() {
      return this.targetsInStatus('mastered');
    }, // masteredTargets()

    closedTargets() {
      return this.targetsInStatus('closed');
    }, // closedTargets()

    masteredAndClosedTargets() {
      return this.masteredTargets + this.closedTargets;
    }, // masteredAndClosedTargets()
  },

  methods: {
    ...mapActions(programs, [
      'deleteProgram',
    ]),

    onShowProgramInfo() {
      this.openComponentDialog(ProgramInfo, { program: this.program });
    }, // onShowProgramInfo()

    /**
     * Add a program to the current SessionPrograms playlist
     */
    onAddToSession(session = null) {
      this.$emit('add-to-session', session);
    }, // onAddToSession()

    onAction() {
      this.$emit('action');
    }, // onAction()

    /**
     * Delete a program
     */
    onDeleteProgram() {
      this.$q
        .dialog({
          title: this.$t('common.dialogTitleConfirm'),
          message: this.$t('programs.programCard.dialogDeleteProgramMessage'),
          cancel: true,
          persistent: true,
        })
        .onOk(() => {
          this.deleteProgram({ program: this.program }).then(() => {
            this.notifySuccess(this.$t('programs.programCard.notifyDeleteProgramSuccess'));
          }).catch((e) => {
            this.notifyNegative(e.message);
          });
        });
    }, // onDeleteProgram()

    targetsInStatus(targetStatus) {
      if (isEmpty(this.program.targetsPerStatus)) {
        return 0;
      }
      const statusObj = this.program.targetsPerStatus.find(
        (statusCount) => statusCount.status === targetStatus,
      );
      return get(statusObj, 'count', 0);
    }, // targetsInStatus()

    /**
     * Opens program modification dialog window
     *
     * @param {Object} program
     */
    async manuallyAddProgramModification(program) {
      return this.openComponentDialog(
        ProgramModificationsDialog,
        { incomingPrograms: [program], isManuallyOpened: true },
        () => this.notifySuccess(this.$t('programs.notifyAddProgramModSuccess')),
      );
    }, // manuallyAddProgramModification()

  },
};
</script>
<style lang="scss" scoped>

.coverphoto div div {
  padding:8px 16px;
}
</style>
